import React, { useRef, useEffect, useState } from 'react';
import { ArrowUpward } from "@material-ui/icons";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import { Box } from "@mui/material";
import { CHAT_TYPE } from "../../../utils/constants";
import useStyles from '../../../assets/styles/ChatBotStyles';
import Loader from '../Loader/Loader.comp';

const parseMarkdown = (text) => {
  if (!text) return '';

  // Replace ** for bold
  let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Replace * or _ for italics
  formatted = formatted.replace(/(\*|_)(.*?)\1/g, '<em>$2</em>');

  // Replace ``` for code blocks
  formatted = formatted.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

  // Replace ` for inline code
  formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Replace --- or *** for horizontal rule
  formatted = formatted.replace(/^(\-{3,}|\*{3,})$/gm, '<hr>');

  // Replace # headers
  formatted = formatted.replace(/^(#{1,6})\s+(.*?)$/gm, (match, hashes, content) => {
    const level = hashes.length;
    return `<h${level}>${content}</h${level}>`;
  });

  // Replace > for blockquotes
  formatted = formatted.replace(/^>\s+(.*?)$/gm, '<blockquote>$1</blockquote>');

  // Replace numbered lists (1. First item)
  formatted = formatted.replace(/^\d+\.\s+(.*?)$/gm, '<li>$1</li>');

  // Replace bullet lists (- Item or * Item)
  formatted = formatted.replace(/^[\-\*]\s+(.*?)$/gm, '<li>$1</li>');

  // Wrap adjacent list items in <ul> or <ol> tags
  formatted = formatted.replace(/<li>.*?<\/li>/gs, match => {
    if (match.includes('</li><li>')) {
      return `<ul>${match}</ul>`;
    }
    return match;
  });

  // Handle line breaks
  formatted = formatted.replace(/\n/g, '<br>');

  return formatted;
};

const ChatBot = ({ data, options, actions }) => {
    const classes = useStyles();
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);
    const [input, setInput] = useState('');
    const [isInputDisabled, setIsInputDisabled] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    // Auto-scroll to bottom when new messages arrive.
    const scrollToBottom = () => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (options.chatPreview !== false) {
            scrollToBottom();
        }
        console.log('mna:log data.chats', data.chats);
    }, [data.chats, options.chatPreview]);

    // Auto-focus on input when available.
    useEffect(() => {
        if (!isInputDisabled && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isInputDisabled]);

    const handleSendMessage = () => {
        if (options.chatType !== 'programOutline' && input.trim() === '') return;
        setIsInputDisabled(true);
        actions.sendMessageAction(input).then(() => {
            setIsInputDisabled(false);
            setInput('');
            // if (response.data.programId) {
            //     setShowLoader(true);
            // }
        });
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    if (showLoader) {
        return <Loader />;
    }

    return (
        <Box className={classes.container} style={{ bottom: options.bottom ? '6%' : '0%' }}>
            {options.chatPreview !== false && (
                <div
                    style={{
                        height: data?.chats?.length > 0 ? options.height : '0px',
                        overflowY: 'auto',
                        padding: '10px',
                        borderRadius: '10px',
                        transition: 'height 0.3s ease',
                    }}
                >
                    {data?.chats.map((chat, index) => {
                        if (chat.type === CHAT_TYPE.USER) {
                            // User messages: show as widget bubbles.
                            return (
                                <div key={index} style={{ textAlign: 'right', marginBottom: '10px' }}>
                                    <div
                                        style={{
                                            backgroundColor: '#F2CB91',
                                            display: 'inline-block',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            maxWidth: '80%',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        <div>{chat.text}</div>
                                    </div>
                                </div>
                            );
                        } else {
                            // System/AI responses: render directly (transparent background) for rich formatting.
                            return (
                                <div key={index} style={{ textAlign: 'left', marginBottom: '10px', padding: '0 10px' }}>
                                    <div
                                        style={{
                                            backgroundColor: 'transparent',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            maxWidth: '100%',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                        dangerouslySetInnerHTML={{ __html: parseMarkdown(chat.text) }}
                                    />
                                </div>
                            );
                        }
                    })}
                    {options.showChatbotLoader && (
                        <div style={{ textAlign: 'left' }}>
                            <div className={classes.loaderContainer}>
                                <div className={classes.loader}></div>
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>
            )}

            <Box className={classes.inputWrapper} sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                    value={input}
                    multiline
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={isInputDisabled ? '' : 'Type your message...'}
                    variant="outlined"
                    fullWidth
                    className={classes.inputField}
                    disabled={isInputDisabled}
                    inputRef={inputRef}
                    InputProps={{
                        endAdornment: (
                            <IconButton onClick={handleSendMessage} className={classes.iconButton} disabled={isInputDisabled}>
                                <ArrowUpward />
                            </IconButton>
                        ),
                    }}
                />
            </Box>
        </Box>
    );
};

export default ChatBot;